/**
 * Structured Output Types for IntelliHealth Recommendations
 *
 * Two recommendation schemas based on event type:
 * 1. LabRecommendation — for lab_result.created events
 * 2. ClinicalPatternRecommendation — for clinical_pattern.created events
 *
 * Both share Pharmetika-compatible medication fields.
 */

// ============================================================================
// Shared Medication Fields
// ============================================================================

export interface PharmetikaMedication {
  medication: string;
  dose: string;
  quantity_authorized: string;
  days_supply?: string;
  unit_of_measure: string;
  refills_authorized: string;
  sig: string;
  rationale?: string;
}

// ============================================================================
// Lab Recommendation (lab_result.created)
// ============================================================================

export interface SoapBlock {
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
}

export interface LabRecommendation {
  schema_version: '2.0';
  event_type: 'lab_result.created';
  summary: string;
  overall_reasoning: string;
  medication_recommendations: PharmetikaMedication[];
  soap: SoapBlock;
}

// ============================================================================
// Clinical Pattern Recommendation (clinical_pattern.created)
// ============================================================================

export type TitrationLevel =
  | 'increase_high'
  | 'increase_moderate'
  | 'increase_low'
  | 'maintain'
  | 'decrease_low'
  | 'decrease_moderate'
  | 'decrease_high'
  | 'discontinue';

export interface TitrationRecommendation extends PharmetikaMedication {
  titration_level: TitrationLevel;
  timeline_weeks: number;
}

export type UrgencyLevel = 'urgent' | 'priority' | 'routine';

export interface ClinicalPatternRecommendation {
  urgency: UrgencyLevel;
  pattern_summary: string;
  primary_titration: TitrationRecommendation;
  alternative_titrations?: TitrationRecommendation[];
  monitoring_symptoms: string[];
  follow_up_days: number;
  patient_communication: string;
  overall_reasoning: string;
}

// ============================================================================
// Discriminated Union
// ============================================================================

export type StructuredRecommendation = LabRecommendation | ClinicalPatternRecommendation;

// ============================================================================
// Type Guards
// ============================================================================

export function isLabRecommendation(value: unknown): value is LabRecommendation {
  return (
    typeof value === 'object' &&
    value !== null &&
    'schema_version' in value &&
    (value as LabRecommendation).schema_version === '2.0'
  );
}

export function isClinicalPatternRecommendation(value: unknown): value is ClinicalPatternRecommendation {
  return (
    typeof value === 'object' &&
    value !== null &&
    'urgency' in value &&
    'primary_titration' in value
  );
}

export function isStructuredRecommendation(value: unknown): value is StructuredRecommendation {
  return isLabRecommendation(value) || isClinicalPatternRecommendation(value);
}
